import { useParams, Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import EnquiryForm from "@/components/feature/EnquiryForm";
import { PROJECTS, PROJECT_GALLERY_SHARED } from "./data";
import { DESTINATION_NAMES } from "@/pages/public/destinations/data";

const STATUS_STYLES: Record<string, string> = {
  "Under Construction": "bg-accent-100 text-accent-900",
  "Ready to Stay": "bg-primary-100 text-primary-800",
  "Coming Soon": "bg-secondary-100 text-secondary-900",
};

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const project = PROJECTS.find((p) => p.slug === slug);

  if (!project) {
    return (
      <div className="w-full px-4 md:px-8 max-w-3xl mx-auto py-24 text-center">
        <i className="ri-map-pin-2-line text-5xl text-foreground-400"></i>
        <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-4">Project not found</h1>
        <p className="text-sm text-foreground-600 mt-2">This project may have moved or is no longer listed.</p>
        <Link to="/projects" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-2.5 hover:bg-primary-700">
          <i className="ri-arrow-left-line"></i> Back to Projects
        </Link>
      </div>
    );
  }

  const gallery = [project.image, ...PROJECT_GALLERY_SHARED];

  return (
    <div className="w-full">
      <PageHero
        eyebrow={`${project.region} · ${project.location}, ${project.state}`}
        title={project.name}
        subtitle={project.description}
        image={project.image}
      />

      {/* Quick stats */}
      <section className="w-full py-10 md:py-12 bg-background-100">
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {[
            { icon: "ri-home-4-line", value: String(project.villas), label: "Villas in Project" },
            { icon: "ri-money-rupee-circle-line", value: project.startPrice, label: "Starting Price" },
            { icon: "ri-line-chart-line", value: project.roi, label: "Est. Annual ROI" },
            { icon: "ri-map-pin-2-line", value: `${project.location}, ${project.state}`, label: "Location" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-background-50 border border-background-200 p-4 md:p-5">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent-100 text-accent-700">
                <i className={`${s.icon} text-lg`}></i>
              </span>
              <p className="font-heading text-xl md:text-2xl text-foreground-950 mt-3 leading-none">{s.value}</p>
              <p className="text-xs text-foreground-600 mt-1.5">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery + highlights + brochure */}
      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-start">
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <div className="col-span-2 rounded-2xl overflow-hidden border border-background-200">
              <img src={gallery[0]} alt={`${project.name} hero`} className="w-full h-56 md:h-72 object-cover" />
            </div>
            {gallery.slice(1).map((img, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-background-200">
                <img src={img} alt={`${project.name} interior ${i + 1}`} className="w-full h-40 md:h-48 object-cover" />
              </div>
            ))}
          </div>

          <div className="space-y-5">
            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold ${STATUS_STYLES[project.status]}`}>
              <i className="ri-flag-line"></i> {project.status}
            </span>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">About {project.name}</h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">{project.description}</p>

            <ul className="space-y-2.5">
              {project.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-check-line text-primary-600 mt-0.5"></i>
                  {h}
                </li>
              ))}
            </ul>

            <div className="rounded-2xl bg-accent-50 border border-accent-200 p-5 md:p-6">
              <p className="text-xs uppercase tracking-wide text-foreground-600 font-semibold">Project Brochure</p>
              <p className="text-sm text-foreground-700 mt-1">
                Download the detailed brochure with villa specs, pricing and projected returns.
              </p>
              <a
                href={project.brochure}
                download
                className="mt-4 inline-flex w-full items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-500 text-background-50 px-5 py-3 hover:bg-accent-600 transition-colors"
              >
                <i className="ri-download-2-line"></i> Download {project.name} Brochure
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section className="w-full py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-accent-600">
              <i className="ri-mail-send-line"></i> Get Started
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-4 leading-tight">
              Enquire About <span className="italic text-primary-700">{project.name}</span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mt-4 leading-relaxed">
              Tell us you&apos;re interested in {project.location} and our investment advisors
              will share pricing, returns and site-visit options for this project.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a href={`tel:8088330330`} className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3 hover:bg-primary-700 transition-colors">
                <i className="ri-phone-fill"></i> Call 80883 30330
              </a>
              <a href={project.brochure} download className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-primary-600 text-primary-700 px-6 py-3 hover:bg-primary-600 hover:text-background-50 transition-colors">
                <i className="ri-download-2-line"></i> Brochure
              </a>
            </div>
          </div>
          <div className="rounded-3xl bg-background-50 border border-background-200 p-5 md:p-8">
            <EnquiryForm
              page={`project-${project.slug}`}
              title={`${project.name} Enquiry`}
              subtitle="No obligation. A dedicated advisor will guide you through pricing, returns and the purchase journey."
              destinationOptions={DESTINATION_NAMES}
              ctaLabel="Send Project Enquiry"
            />
          </div>
        </div>
      </section>
    </div>
  );
}